import { useState, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertCircle, Download } from 'lucide-react';

const PRODUCT_FIELDS = [
  'product_code', 'product_type', 'design_group_code', 'design_group_name',
  'product_name', 'brand', 'category', 'subcategory', 'deity',
  'regional_tags', 'occasion_tags',
  'mrp', 'size', 'material',
  'short_description', 'description', 'description_en',
  'design_group_short_description', 'design_group_description',
  'status', 'sort_order', 'group_sort_order', 'sku_sort_order',
  'search_keywords',
  'cover_image', 'cover_image_drive_link', 'design_group_image',
  'gallery_image_drive_links'
];

export default function BulkUploadPage() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [skipDuplicates, setSkipDuplicates] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const mappedKeys = useMemo(() => Object.keys(fieldMapping).filter(k => fieldMapping[k]), [fieldMapping]);
  const previewRows = useMemo(() => (previewData?.rows || []).slice(0, 50), [previewData]);

  const handleFileUpload = async () => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/products/upload-preview', formData);
      setPreviewData(data);
      const autoMapping = {};
      PRODUCT_FIELDS.forEach(field => {
        const match = data.headers.find(h => h.toLowerCase().replace(/[\s_-]/g, '') === field.toLowerCase().replace(/[\s_-]/g, ''));
        if (match) autoMapping[field] = match;
      });
      setFieldMapping(autoMapping);
      setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to parse file'); }
    setUploading(false);
  };

  const handleImport = async () => {
    setUploading(true);
    try {
      const { data } = await api.post('/products/bulk-import', {
        products: previewData.rows,
        field_mapping: fieldMapping,
        skip_duplicates: skipDuplicates
      });
      setImportResult(data);
      setStep(4);
      toast.success(`Imported ${data.imported} products`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    setUploading(false);
  };

  return (
    <div data-testid="bulk-upload-page" className="max-w-4xl animate-fade-in">
      <h1 className="font-serif text-3xl text-[#1A1A1A] tracking-tight mb-2">Bulk Upload</h1>
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-[#5C5C5C]">Import products from Excel files</p>
        <Button variant="outline" onClick={async () => {
          try {
            const response = await api.get('/products/excel-template', { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a'); a.href = url; a.download = 'AARYA24kt_Product_Template.xlsx'; a.click(); URL.revokeObjectURL(url);
            toast.success('Template downloaded');
          } catch { toast.error('Download failed'); }
        }} className="border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] rounded-sm" data-testid="download-template-btn">
          <Download size={16} className="mr-2" />Download Excel Template
        </Button>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-8">
        {['Upload File', 'Map Columns', 'Preview', 'Complete'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step > i + 1 ? 'bg-[#2D6A4F] text-white' : step === i + 1 ? 'bg-[#C5A059] text-white' : 'bg-[#E5E0D8] text-[#5C5C5C]'}`}>
              {step > i + 1 ? <Check size={14} /> : i + 1}
            </div>
            <span className={`text-xs tracking-wide ${step === i + 1 ? 'text-[#1A1A1A] font-medium' : 'text-[#5C5C5C]'}`}>{label}</span>
            {i < 3 && <ArrowRight size={14} className="text-[#E5E0D8] mx-2" />}
          </div>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8">
          <div className="border-2 border-dashed border-[#E5E0D8] rounded-sm p-12 text-center">
            <FileSpreadsheet size={48} className="mx-auto text-[#C5A059] mb-4" />
            <p className="text-[#1A1A1A] font-medium mb-2">Upload Excel File</p>
            <p className="text-sm text-[#5C5C5C] mb-6">Supports .xlsx files with product data</p>
            <Input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="max-w-xs mx-auto rounded-sm border-[#E5E0D8]" data-testid="excel-file-input" />
            {file && <p className="text-sm text-[#2D6A4F] mt-3">{file.name}</p>}
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={handleFileUpload} disabled={!file || uploading} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="upload-excel-btn">
              {uploading ? 'Processing...' : 'Upload & Parse'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && previewData && (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
          <h2 className="font-serif text-lg text-[#1A1A1A] mb-4">Map Excel Columns to Product Fields</h2>
          <p className="text-sm text-[#5C5C5C] mb-6">Found {previewData.headers.length} columns and {previewData.total} rows</p>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {PRODUCT_FIELDS.map(field => (
              <div key={field} className="flex items-center gap-4">
                <span className="w-40 text-sm font-medium text-[#1A1A1A]">{field.replace(/_/g, ' ')}</span>
                <ArrowRight size={14} className="text-[#E5E0D8]" />
                <Select value={fieldMapping[field] || 'skip'} onValueChange={v => setFieldMapping(prev => ({ ...prev, [field]: v === 'skip' ? undefined : v }))}>
                  <SelectTrigger className="w-60 rounded-sm border-[#E5E0D8]"><SelectValue placeholder="Skip this field" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="skip">-- Skip --</SelectItem>
                    {previewData.headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
                {fieldMapping[field] && <Badge className="bg-[#F2E8D5] text-[#C5A059] text-[10px]">Mapped</Badge>}
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[#5C5C5C]" data-testid="skip-duplicates-toggle">
              <Checkbox checked={skipDuplicates} onCheckedChange={setSkipDuplicates} />
              <span>
                Skip rows whose product code already exists
                <span className="block text-[10px] text-[#888] italic">When unchecked (default), existing products are <strong>updated</strong> with the latest values; new codes are created. Product code is the unique identifier.</span>
              </span>
            </label>
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm border-[#E5E0D8]">Back</Button>
            <Button onClick={() => setStep(3)} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="preview-import-btn">Preview Data</Button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && previewData && (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg text-[#1A1A1A]">Preview ({previewData.total} rows)</h2>
            {previewData.duplicates > 0 && (
              <Badge className="bg-[#FFF3E0] text-[#E65100] text-xs"><AlertCircle size={12} className="mr-1" />{previewData.duplicates} duplicates found</Badge>
            )}
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="preview-table">
              <thead><tr className="border-b border-[#E5E0D8]">
                <th className="text-left py-2 px-3 text-[10px] tracking-wider uppercase text-[#5C5C5C] sticky top-0 bg-white">Status</th>
                {mappedKeys.map(k => (
                  <th key={k} className="text-left py-2 px-3 text-[10px] tracking-wider uppercase text-[#5C5C5C] sticky top-0 bg-white">{k.replace(/_/g, ' ')}</th>
                ))}
              </tr></thead>
              <tbody>
                {previewRows.map((row, idx) => (
                  <tr key={`${row.product_code || ''}-${idx}`} className={`border-b border-[#E5E0D8] ${row._is_duplicate ? 'bg-[#FFF8E1]' : ''}`}>
                    <td className="py-2 px-3">
                      {row._is_duplicate ? <Badge className="bg-[#FFF3E0] text-[#E65100] text-[10px]">Duplicate</Badge> : <Badge className="bg-[#E8F5E9] text-[#2D6A4F] text-[10px]">New</Badge>}
                    </td>
                    {mappedKeys.map(k => (
                      <td key={k} className="py-2 px-3 text-[#5C5C5C] max-w-[200px] truncate">{String(row[fieldMapping[k]] || '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm border-[#E5E0D8]">Back</Button>
            <Button onClick={handleImport} disabled={uploading} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="import-products-btn">
              {uploading ? 'Importing...' : `Import ${previewData.total - (skipDuplicates ? previewData.duplicates : 0)} ${skipDuplicates ? 'New' : 'Products (Upsert)'}`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 4 && importResult && (
        <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 text-center" data-testid="import-complete">
          <Check size={48} className="mx-auto text-[#2D6A4F] mb-4" />
          <h2 className="font-serif text-2xl text-[#1A1A1A] mb-4">Import Complete</h2>
          <div className="flex gap-6 justify-center mb-6 flex-wrap">
            <div data-testid="import-total"><p className="text-2xl font-semibold text-[#1A1A1A]">{importResult.total ?? (importResult.imported + importResult.skipped + importResult.errors.length)}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Total Rows</p></div>
            <div data-testid="import-created"><p className="text-2xl font-semibold text-[#2D6A4F]">{importResult.created ?? importResult.imported}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Created</p></div>
            <div data-testid="import-updated"><p className="text-2xl font-semibold text-[#2D6A4F]">{importResult.updated ?? 0}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Updated</p></div>
            <div data-testid="import-skipped"><p className="text-2xl font-semibold text-[#C5A059]">{importResult.skipped}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Skipped</p></div>
            <div data-testid="import-duplicates"><p className="text-2xl font-semibold text-[#C5A059]">{importResult.duplicate_codes_in_file?.length || 0}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Duplicate Codes In File</p></div>
            <div data-testid="import-errors"><p className="text-2xl font-semibold text-[#9E2A2B]">{importResult.errors.length}</p><p className="text-xs text-[#5C5C5C] uppercase tracking-wider">Errors</p></div>
          </div>
          {importResult.duplicate_codes_in_file?.length > 0 && (
            <div className="text-left bg-[#FFF8E1] border border-[#C5A059]/40 rounded-sm p-4 mb-4 max-h-32 overflow-y-auto" data-testid="duplicate-codes-warning">
              <p className="text-xs font-semibold text-[#1A1A1A] mb-1">Duplicate product codes detected inside the uploaded file:</p>
              <p className="text-xs text-[#5C5C5C] break-all">{importResult.duplicate_codes_in_file.slice(0, 30).join(', ')}{importResult.duplicate_codes_in_file.length > 30 ? '…' : ''}</p>
              <p className="text-[10px] text-[#888] mt-1 italic">Only the first occurrence of each code was processed; subsequent rows were counted as Skipped.</p>
            </div>
          )}
          {importResult.errors.length > 0 && (
            <div className="text-left bg-red-50 border border-red-200 rounded-sm p-4 mb-6 max-h-40 overflow-y-auto">
              {importResult.errors.map((e, i) => <p key={`${i}-${e.slice(0,40)}`} className="text-sm text-red-700">{e}</p>)}
            </div>
          )}
          <Button onClick={() => { setStep(1); setFile(null); setPreviewData(null); setImportResult(null); }} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm">Upload Another File</Button>
        </div>
      )}
      {/* iter29 — Add Quantities card (sibling of main upload). Linked to
          /api/products/quantities-template + /api/products/quantities-preview
          + /api/products/quantities-apply. Live quantities flow through every
          Show-Quantity-enabled catalog via their existing shared links. */}
      <AddQuantitiesSection />
    </div>
  );
}

function AddQuantitiesSection() {
  const [qtyFile, setQtyFile] = useState(null);
  const [qtyPreview, setQtyPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get('/products/quantities-template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'AARYA24kt_Quantity_Template.xlsx'; a.click(); URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  const parse = async () => {
    if (!qtyFile) return;
    setBusy(true);
    const fd = new FormData(); fd.append('file', qtyFile);
    try {
      const { data } = await api.post('/products/quantities-preview', fd);
      setQtyPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to parse file'); }
    setBusy(false);
  };

  const apply = async () => {
    if (!qtyPreview?.ready_to_apply) return;
    setBusy(true);
    const payloadRows = qtyPreview.rows.filter(r => r.errors.length === 0).map(r => ({ product_code: r.product_code, quantity: r.new_quantity }));
    try {
      const { data } = await api.post('/products/quantities-apply', { rows: payloadRows });
      setResult(data);
      setQtyPreview(null); setQtyFile(null);
      toast.success(`Updated ${data.updated_codes.length} quantities. ${data.affected_catalogs} catalog${data.affected_catalogs === 1 ? '' : 's'} with Show Quantity ON will refresh on their existing links.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Apply failed'));
    }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-sm p-8 mt-8" data-testid="add-quantities-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-serif text-xl text-[#1A1A1A]">Add Quantities</h2>
          <p className="text-xs text-[#5C5C5C] mt-1">Separate from general product uploads. Only two columns: <strong>Product Code</strong> + <strong>Quantity</strong>. Latest values REPLACE previous ones for matching codes; absent codes keep their existing quantity. Shared catalogs with Show Quantity enabled use the latest values — their links stay the same.</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} className="border-[#C5A059] text-[#C5A059] hover:bg-[#F2E8D5] rounded-sm shrink-0" data-testid="download-quantity-template-btn">
          <Download size={14} className="mr-1" />Template
        </Button>
      </div>
      {!qtyPreview && !result && (
        <div className="border border-dashed border-[#E5E0D8] rounded-sm p-6 flex items-center gap-4">
          <FileSpreadsheet size={28} className="text-[#C5A059]" />
          <Input type="file" accept=".xlsx,.xls" onChange={e => setQtyFile(e.target.files?.[0] || null)} className="rounded-sm border-[#E5E0D8] max-w-xs" data-testid="quantity-file-input" />
          <Button onClick={parse} disabled={!qtyFile || busy} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="parse-quantity-btn">
            {busy ? 'Parsing…' : 'Review'}
          </Button>
        </div>
      )}
      {qtyPreview && (
        <div data-testid="quantity-preview">
          <div className="flex items-center gap-3 mb-3 text-xs">
            <Badge className="bg-[#E8F5E9] text-[#2D6A4F]">{qtyPreview.valid_count} valid</Badge>
            {qtyPreview.error_count > 0 && <Badge className="bg-red-100 text-red-700"><AlertCircle size={12} className="mr-1" />{qtyPreview.error_count} errors</Badge>}
            {qtyPreview.duplicate_codes.length > 0 && <Badge className="bg-[#FFF3E0] text-[#E65100]">{qtyPreview.duplicate_codes.length} duplicate codes</Badge>}
          </div>
          <div className="max-h-80 overflow-y-auto border border-[#E5E0D8] rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-[#F5F2EB] sticky top-0">
                <tr>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Row</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Code</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Name</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Old → New</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-[#5C5C5C]">Status</th>
                </tr>
              </thead>
              <tbody>
                {qtyPreview.rows.map(r => (
                  <tr key={r.row_number} className={`border-t border-[#E5E0D8] ${r.errors.length ? 'bg-red-50' : ''}`} data-testid={`quantity-preview-row-${r.row_number}`}>
                    <td className="py-1.5 px-3 text-[#5C5C5C]">{r.row_number}</td>
                    <td className="py-1.5 px-3 font-mono text-[#C5A059]">{r.product_code}</td>
                    <td className="py-1.5 px-3 text-[#1A1A1A]">{r.product_name || <span className="italic text-[#9E2A2B]">unknown</span>}</td>
                    <td className="py-1.5 px-3 text-[#1A1A1A]">
                      <span className="text-[#888]">{r.old_quantity ?? '—'}</span>
                      <span className="mx-2 text-[#C5A059]">→</span>
                      <strong>{r.new_quantity ?? '—'}</strong>
                    </td>
                    <td className="py-1.5 px-3">
                      {r.errors.length === 0 ? (
                        <Badge className="bg-[#E8F5E9] text-[#2D6A4F] text-[10px]">OK</Badge>
                      ) : (
                        <span className="text-[10px] text-[#9E2A2B]">{r.errors.join('; ')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <Button variant="outline" onClick={() => { setQtyPreview(null); setQtyFile(null); }} className="rounded-sm border-[#E5E0D8]" data-testid="quantity-cancel-btn">Cancel</Button>
            <Button onClick={apply} disabled={!qtyPreview.ready_to_apply || busy} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm" data-testid="apply-quantity-btn">
              {busy ? 'Applying…' : qtyPreview.ready_to_apply ? `Apply ${qtyPreview.valid_count} Quantities` : 'Fix errors to apply'}
            </Button>
          </div>
        </div>
      )}
      {result && (
        <div className="border border-[#E8F5E9] bg-[#F0F9F3] rounded-sm p-5 mt-3" data-testid="quantity-result">
          <p className="text-sm text-[#2D6A4F] font-medium"><Check size={14} className="inline mr-1" />{result.updated_codes.length} quantit{result.updated_codes.length === 1 ? 'y' : 'ies'} updated. {result.affected_catalogs} Show-Quantity catalog{result.affected_catalogs === 1 ? '' : 's'} refreshed on their original links.</p>
          <Button variant="outline" size="sm" onClick={() => setResult(null)} className="mt-3 rounded-sm border-[#E5E0D8]">Upload Another</Button>
        </div>
      )}
    </div>
  );
}